/**
 * Tracking PRO — live courier tracking panel for the ASN detail modal.
 *
 * Shows current courier status, the carrier's estimated delivery date and the
 * full scan timeline, with a manual refresh for when someone wants an answer
 * now rather than at the next sweep.
 */
import { useState, useEffect, useCallback } from 'react';
import { purchasingAPI, ASNTracking } from '../../../lib/api';
import { useFormatters } from '../../../contexts/PreferencesContext';
import { Button } from '../../ui/button';
import { AlertCircle, ExternalLink, Loader2, MapPin, RefreshCw, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { CourierStatusBadge, isTrackableASN } from './CourierStatusBadge';
import { getApiErrorMessage } from '../../../utils/errorUtils';

interface ASNTrackingPanelProps {
  asnId: number;
  courier?: string;
  trackingNumber?: string;
  courierOther?: string;
  /** Whether live tracking is switched on platform-wide. */
  proEnabled?: boolean;
}

/**
 * Live courier tracking for one ASN.
 *
 * @param asnId - ASN to track
 * @param courier - ASN courier code, used to decide whether tracking applies
 * @param trackingNumber - ASN tracking number
 */
export default function ASNTrackingPanel({
  asnId, courier, trackingNumber, courierOther, proEnabled,
}: ASNTrackingPanelProps) {
  const { formatDate, formatDateTime } = useFormatters();
  const [tracking, setTracking] = useState<ASNTracking | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const trackable = isTrackableASN(courier, trackingNumber, courierOther);

  const loadTracking = useCallback(async () => {
    if (!trackable) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await purchasingAPI.getASNTracking(asnId);
      setTracking(response.data);
    } catch (err: unknown) {
      // 404 is the normal "not registered for tracking yet" case, not an error
      // worth shouting about — the empty state explains it.
      setTracking(null);
    } finally {
      setLoading(false);
    }
  }, [asnId, trackable]);

  useEffect(() => {
    loadTracking();
  }, [loadTracking]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const response = await purchasingAPI.refreshASNTracking(asnId);
      setTracking(response.data);
      toast.success('Courier tracking updated');
    } catch (err: unknown) {
      // Surface the API's own explanation ("This ASN has no trackable courier
      // and tracking number") rather than axios's "Request failed with status
      // code 400", which tells the user nothing.
      toast.error(getApiErrorMessage(err, 'Could not refresh courier tracking'));
    } finally {
      setRefreshing(false);
    }
  };

  if (!trackable) {
    return null;
  }

  const summary = tracking
    ? {
        status: tracking.status,
        status_label: tracking.status_label,
        status_message: tracking.status_message,
        substatus: tracking.substatus,
        estimated_delivery_date: tracking.estimated_delivery_date,
        last_checkpoint_at: tracking.last_checkpoint_at,
        last_synced_at: tracking.last_synced_at,
        sync_state: tracking.sync_state,
        sync_error: tracking.sync_error,
        tracking_url: tracking.courier_tracking_url || tracking.aftership_tracking_url,
        is_terminal: tracking.is_terminal,
      }
    : null;

  return (
    <div className="border rounded-lg p-4 space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold">Courier Tracking</h3>
          <span className="px-1.5 py-0.5 text-[10px] bg-gradient-to-r from-amber-400 to-orange-500 text-white rounded-full font-bold flex items-center gap-0.5">
            <Sparkles className="h-2.5 w-2.5" />
            PRO
          </span>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={refreshing || !proEnabled}
          title={proEnabled ? undefined : 'Live courier tracking is switched off for this platform'}
        >
          {refreshing ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
          ) : (
            <RefreshCw className="h-3.5 w-3.5 mr-1" />
          )}
          Refresh
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading tracking…
        </div>
      ) : !tracking ? (
        <p className="text-sm text-muted-foreground">
          {proEnabled
            ? 'Not registered with the courier yet. It is picked up automatically within '
              + 'the hour — or hit Refresh to do it now.'
            : 'Live courier tracking is switched off for this platform. An administrator '
              + 'can enable it in Admin Setup → Tracking PRO.'}
        </p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Courier Status</span>
              <p className="mt-1">
                <CourierStatusBadge tracking={summary} trackable />
              </p>
            </div>
            <div>
              <span className="text-muted-foreground">Estimated Delivery</span>
              <p className="font-medium">
                {tracking.estimated_delivery_date
                  ? formatDate(tracking.estimated_delivery_date)
                  : '-'}
              </p>
            </div>
            <div>
              <span className="text-muted-foreground">Last Scan</span>
              <p className="font-medium">
                {tracking.last_checkpoint_at ? formatDateTime(tracking.last_checkpoint_at) : '-'}
              </p>
            </div>
            <div>
              <span className="text-muted-foreground">Last Checked</span>
              <p className="font-medium">
                {tracking.last_synced_at ? formatDateTime(tracking.last_synced_at) : '-'}
              </p>
            </div>
            {tracking.signed_by && (
              <div>
                <span className="text-muted-foreground">Signed For By</span>
                <p className="font-medium">{tracking.signed_by}</p>
              </div>
            )}
            {tracking.failed_delivery_attempts > 0 && (
              <div>
                <span className="text-muted-foreground">Failed Attempts</span>
                <p className="font-medium text-orange-700">{tracking.failed_delivery_attempts}</p>
              </div>
            )}
          </div>

          {tracking.sync_state === 'error' && tracking.sync_error && (
            <div className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{tracking.sync_error}</span>
            </div>
          )}

          {(tracking.courier_tracking_url || tracking.aftership_tracking_url) && (
            <a
              href={tracking.courier_tracking_url || tracking.aftership_tracking_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
            >
              Open carrier tracking page
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}

          <div>
            <h4 className="text-sm font-medium mb-2">Tracking History</h4>
            {tracking.checkpoints.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No courier scans recorded yet.
              </p>
            ) : (
              <ol className="space-y-3">
                {tracking.checkpoints.map((checkpoint) => (
                  <li key={checkpoint.id} className="flex gap-3 text-sm">
                    <div className="flex flex-col items-center pt-1">
                      <span className="h-2 w-2 rounded-full bg-blue-500" />
                      <span className="flex-1 w-px bg-border mt-1" />
                    </div>
                    <div className="pb-1">
                      <p className="font-medium">
                        {checkpoint.message || checkpoint.status_label || 'Scan'}
                      </p>
                      <p className="text-muted-foreground flex items-center gap-2 flex-wrap">
                        {checkpoint.checkpoint_time && (
                          <span>{formatDateTime(checkpoint.checkpoint_time)}</span>
                        )}
                        {checkpoint.location && (
                          <span className="inline-flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {checkpoint.location}
                          </span>
                        )}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </>
      )}
    </div>
  );
}
